import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react'
import { glassBlur } from './glass'

/**
 * Dark mode is a warm, maroon-tinted "glass" theme rather than a neutral gray
 * inversion: the page is a deep plum-black wash, and panels float on it as
 * translucent, blurred sheets. Two rules keep it coherent:
 *
 *  1. Dark surfaces are white alphas, never opaque grays — they let the page
 *     wash show through so every panel picks up the same warm tint.
 *  2. The red accent splits in two: `primary.solid` (button fills, needs white
 *     text on top) stays dark enough for contrast, `primary.fg` (text, icons,
 *     borders) lifts to a bright red so it stays legible on the dark wash.
 */

/** Page wash: warm radial glows over a plum-black base. */
const darkPageGradient = [
  'radial-gradient(1100px 620px at 12% -8%, rgba(122, 31, 39, 0.42), transparent 62%)',
  'radial-gradient(900px 520px at 88% 2%, rgba(74, 24, 44, 0.32), transparent 66%)',
  'radial-gradient(1000px 700px at 50% 110%, rgba(100, 26, 32, 0.20), transparent 70%)',
  'linear-gradient(180deg, #1B0D11 0%, #150A0C 55%, #110709 100%)',
].join(', ')

const customConfig = defineConfig({
  cssVarsRoot: ':root',
  preflight: true,

  /**
   * Chakra writes the token blocks in the order it first meets each
   * condition. Our tokens use `base` + `_dark`, so the dark block comes out
   * before the `_light` block of Chakra's own palettes (`gray.fg`,
   * `colorPalette.*`). Both selectors (`.dark` vs `:root, .light`) have the
   * same specificity, so the later light block won in dark mode, and outline
   * and ghost buttons rendered dark text on the dark wash. Anchoring `.dark`
   * to `:root` makes it more specific, so it wins whatever the order.
   */
  conditions: {
    dark: ':root.dark &, .dark .chakra-theme:not(.light) &',
  },

  globalCss: {
    'html, body': {
      bg: 'bg',
      color: 'fg',
    },
    // The wash is painted on <body> and pinned to the viewport so it never
    // tiles or scrolls away on long pages.
    body: {
      _dark: {
        backgroundImage: darkPageGradient,
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      },
    },
  },

  theme: {
    keyframes: {
      /** A card settling into place: up from below while fading in. */
      'rise-in': {
        from: { opacity: 0, transform: 'translateY(14px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
      },
      /** A progress bar filling from its left edge. */
      'grow-x': {
        from: { transform: 'scaleX(0)' },
        to: { transform: 'scaleX(1)' },
      },
    },
    tokens: {
      fonts: {
        body: { value: 'var(--font-sans)' },
        heading: { value: '"Fraunces", Georgia, serif' },
        mono: { value: 'ui-monospace, monospace' },
      },
      blurs: {
        glass: { value: glassBlur },
      },
      colors: {
        // ─── Neutral scale ────────────────────────────────────────
        bg_base: { value: '#F8F6F6' },
        white: { value: '#FFFFFF' },
        black: { value: '#000000' },

        neutral: {
          0: { value: '#FFFFFF' },
          50: { value: '#F8F6F6' },
          100: { value: '#F0EEEE' },
          200: { value: '#E0DDDD' },
          300: { value: '#C4C0C0' },
          400: { value: '#A2A2A2' },
          500: { value: '#8D8D8D' },
          600: { value: '#717171' },
          700: { value: '#555555' },
          800: { value: '#2E2E2E' },
          900: { value: '#1A1A1A' },
          950: { value: '#0D0D0D' },
        },

        /** Warm plum-black scale the dark theme is built on. */
        plum: {
          950: { value: '#110709' },
          900: { value: '#150A0C' },
          850: { value: '#1B0D11' },
          800: { value: '#241417' },
          700: { value: '#301A1F' },
        },

        primary: {
          // red
          15: { value: '#641A2026' },
          20: { value: '#641A2033' },
          100: { value: '#641A20' },
          hover: { value: '#4E1219' },
          // Dark-mode counterparts: a lifted fill and a bright accent.
          solidDark: { value: '#A32029' },
          solidDarkHover: { value: '#BC2831' },
          fgDark: { value: '#F1666B' },
        },

        violent: {
          5: { value: '#3D00CC0D' },
          100: { value: '#3D00CC' },
        },
      },
    },

    semanticTokens: {
      // ─── Elevation ───────────────────────────────────────
      // Light mode: soft diffuse shadow. Dark mode: a deep, wide drop so glass
      // panels separate from the wash without needing a heavy outline.
      shadows: {
        card: {
          value: {
            base: '0 1px 2px rgba(15, 15, 20, 0.04), 0 4px 14px rgba(15, 15, 20, 0.08)',
            _dark:
              '0 8px 32px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
          },
        },
      },

      colors: {
        // ─── Foreground (Text) ─────────────────────────────

        fg: {
          value: { base: '{colors.neutral.900}', _dark: '#F6ECEC' },
        },
        'fg.certificate': {
          value: { base: '{colors.black}', _dark: '{colors.black}' },
        },
        'fg.muted': {
          value: {
            base: '{colors.neutral.500}',
            _dark: 'rgba(246, 236, 236, 0.64)',
          },
        },
        'fg.subtle': {
          value: {
            base: '{colors.neutral.400}',
            _dark: 'rgba(246, 236, 236, 0.44)',
          },
        },
        'fg.disabled': {
          value: {
            base: '{colors.neutral.300}',
            _dark: 'rgba(246, 236, 236, 0.28)',
          },
        },

        // ─── Background ────────────────────────────────────
        bg: {
          value: { base: '{colors.bg_base}', _dark: '{colors.plum.900}' },
        },
        'bg.subtle': {
          value: {
            base: '{colors.neutral.100}',
            _dark: 'rgba(255, 255, 255, 0.07)',
          },
        },
        'bg.muted': {
          value: {
            base: '{colors.neutral.200}',
            _dark: 'rgba(255, 255, 255, 0.12)',
          },
        },
        /**
         * Opaque elevated surface — for anything that must fully hide what is
         * behind it (modals, popovers, select menus, dropdowns).
         */
        'bg.surface': {
          value: { base: '{colors.white}', _dark: '{colors.plum.800}' },
        },
        /**
         * Chakra's own recipes (Dialog, Menu, Popover, Select…) fill with
         * `bg.panel`, so it is pinned to the same opaque surface — otherwise
         * those overlays would inherit Chakra's cold gray in dark mode.
         */
        'bg.panel': {
          value: { base: '{colors.white}', _dark: '{colors.plum.800}' },
        },
        /**
         * Glass panel — cards, tables, the header. Translucent in dark mode so
         * the page wash reads through; always pair with a backdrop blur
         * (see `glassPanel` in `shared/lib/chakra/glass.ts`).
         */
        'bg.glass': {
          value: { base: '{colors.white}', _dark: 'rgba(255, 255, 255, 0.05)' },
        },
        /** A second, denser glass step for panels nested inside a glass panel. */
        'bg.glass.raised': {
          value: {
            base: '{colors.white}',
            _dark: 'rgba(255, 255, 255, 0.08)',
          },
        },
        'bg.disabled': {
          value: {
            base: '{colors.neutral.100}',
            _dark: 'rgba(255, 255, 255, 0.06)',
          },
        },
        /** Recessed region inside a panel — a list body, a well, a preview strip. */
        'bg.inset': {
          value: {
            base: 'rgba(0, 0, 0, 0.024)',
            _dark: 'rgba(0, 0, 0, 0.18)',
          },
        },
        /** Zebra striping for table rows — a hair off the surface, not a full step. */
        'bg.stripe': {
          value: {
            base: 'rgba(0, 0, 0, 0.022)',
            _dark: 'rgba(255, 255, 255, 0.035)',
          },
        },

        // ─── Borders ───────────────────────────────────────
        border: {
          value: {
            base: '{colors.neutral.200}',
            _dark: 'rgba(255, 255, 255, 0.14)',
          },
        },
        'border.muted': {
          value: {
            base: '{colors.neutral.100}',
            _dark: 'rgba(255, 255, 255, 0.07)',
          },
        },
        'border.strong': {
          value: {
            base: '{colors.neutral.300}',
            _dark: 'rgba(255, 255, 255, 0.22)',
          },
        },
        /** Hairline that gives a glass panel its lit edge. */
        'border.glass': {
          value: {
            base: 'transparent',
            _dark: 'rgba(255, 255, 255, 0.10)',
          },
        },

        // ─── Primary ───────────────────────────────────────
        /** Text, icons and borders — lifts in dark mode to stay legible. */
        'primary.fg': {
          value: {
            base: '{colors.primary.100}',
            _dark: '{colors.primary.fgDark}',
          },
        },
        /** Solid fills that carry white text on top. */
        'primary.solid': {
          value: {
            base: '{colors.primary.100}',
            _dark: '{colors.primary.solidDark}',
          },
        },
        'primary.subtle': {
          value: {
            base: '{colors.primary.15}',
            _dark: 'rgba(241, 102, 107, 0.14)',
          },
        },
        'primary.hover': {
          value: {
            base: '{colors.primary.hover}',
            _dark: '{colors.primary.solidDarkHover}',
          },
        },

        // ─── Violet ────────────────────────────────────────
        'violet.fg': {
          value: { base: '{colors.violent.100}', _dark: '#A98BFF' },
        },
        'violet.subtle': {
          value: {
            base: '{colors.violent.5}',
            _dark: 'rgba(169, 139, 255, 0.14)',
          },
        },

        // ─── Accent cards ──────────────────────────────────
        // `.5` is the tint fill, `.100` the text/border colour.
        'card.royal.5': {
          value: { base: '#0077CC0D', _dark: 'rgba(79, 165, 255, 0.10)' },
        },
        'card.royal.100': {
          value: { base: '#0077CC', _dark: '#6FB7FF' },
        },
        'card.yellow.5': {
          value: { base: '#D58E150D', _dark: 'rgba(240, 180, 74, 0.10)' },
        },
        'card.yellow.100': {
          value: { base: '#D58E15', _dark: '#F0B44A' },
        },
        'card.red.5': {
          value: { base: '#FFFFFF', _dark: 'rgba(255, 255, 255, 0.05)' },
        },
        'card.red.100': {
          value: { base: '#641A20', _dark: '#7C2028' },
        },

        // ─── Badges ────────────────────────────────────────
        // `.20` is the pill fill, `.100` the label colour.
        'badge.blue.20': {
          value: { base: '#6B8FDC33', _dark: 'rgba(139, 168, 232, 0.20)' },
        },
        'badge.blue.100': {
          value: { base: '#6B8FDC', _dark: '#9CBAF2' },
        },
        'badge.teal.20': {
          value: { base: '#02687333', _dark: 'rgba(45, 179, 194, 0.20)' },
        },
        'badge.teal.100': {
          value: { base: '#026873', _dark: '#4FCBD8' },
        },
        'badge.green.20': {
          value: { base: '#35C44A33', _dark: 'rgba(53, 196, 74, 0.20)' },
        },
        'badge.green.100': {
          value: { base: '#35C44A', _dark: '#5BDC6F' },
        },
        'badge.orange.20': {
          value: { base: '#D9770633', _dark: 'rgba(251, 146, 60, 0.20)' },
        },
        'badge.orange.100': {
          value: { base: '#C2410C', _dark: '#FDA15B' },
        },
        'badge.red.20': {
          value: { base: '#AB302B33', _dark: 'rgba(224, 90, 84, 0.22)' },
        },
        'badge.red.100': {
          value: { base: '#AB302B', _dark: '#F0776F' },
        },
        'badge.gray.20': {
          value: { base: '#6B728033', _dark: 'rgba(255, 255, 255, 0.12)' },
        },
        'badge.gray.100': {
          value: { base: '#6B7280', _dark: 'rgba(246, 236, 236, 0.70)' },
        },
      },
    },
  },
})

export const system = createSystem(defaultConfig, customConfig)
